import { usePrevious } from '@trmediaab/zebra-hooks';
import { useCallback, useEffect, useState } from 'react';

import CollaborationButton from './CollaborationButton';
import CollaborationCenter from './CollaborationCenter';
import styles from './index.module.scss';

const Collaboration = ({
  type,
  betId,
  comments,
  claimStatus,
  isLackingSubscription,
  collaborators,
  addComment,
  updateComment,
  deleteComment,
  listComments,
  fetchCollaborators,
  addCollaborationSubscriptions,
  removeCollaborationSubscriptions,
  claimBet,
  expireClaim,
  releaseBet,
  openShareDialog,
}) => {
  const [open, setOpen] = useState(false);
  const prevComments = usePrevious(comments);

  useEffect(() => {
    listComments();
    fetchCollaborators();
  }, [listComments, fetchCollaborators]);

  useEffect(() => {
    if (
      (claimStatus?.claimed &&
        collaborators?.length > 0 &&
        !collaborators.find(
          collaborator => collaborator.id === claimStatus.currentOwner,
        )?.accepted) ||
      (comments?.length > prevComments?.length &&
        !collaborators?.find(
          collaborator => collaborator.id === comments[0].userId,
        )?.accepted)
    ) {
      // If the bet is claimed by an unknown collaborator id,
      // or if a new comment came in from an unknown collaborator id,
      // we must refetch the collaborator list.
      fetchCollaborators();
    }
  }, [claimStatus, collaborators, comments, fetchCollaborators, prevComments]);

  const isCollaborativeBet = collaborators?.length > 1;

  useEffect(() => {
    if (isCollaborativeBet) {
      addCollaborationSubscriptions();
      return () => removeCollaborationSubscriptions();
    }
  }, [
    isCollaborativeBet,
    addCollaborationSubscriptions,
    removeCollaborationSubscriptions,
  ]);

  const calculateRemainingClaimTime = useCallback(
    () =>
      claimStatus?.autoReleaseTime
        ? claimStatus.autoReleaseTime - Date.now()
        : 0,
    [claimStatus?.autoReleaseTime],
  );

  const [remainingClaimTime, setRemainingClaimTime] = useState(
    calculateRemainingClaimTime(),
  );

  // This effect is the only place in the code where we should need to measure the
  // device time against the autoReleaseTime timestamp. It will allow us to pass down
  // remainingClaimTime to the CollaborationCenter (to render its countdown), and it
  // is responsible for locally expiring the claimStatus so that all other places in
  // the code can rely solely on the claimStatus.claimed boolean, and not have to
  // bother checking autoReleaseTime.
  useEffect(() => {
    setRemainingClaimTime(calculateRemainingClaimTime());
    let interval;
    if (claimStatus?.claimed && isCollaborativeBet) {
      interval = setInterval(() => {
        const newRemainingTime = calculateRemainingClaimTime();
        setRemainingClaimTime(newRemainingTime);
        if (newRemainingTime <= 0) {
          clearInterval(interval);
          // This action call is very important as this is the only immediate place where
          // claims expire. Claims also expire via backend socket pushes, but they're not
          // accurate enough for a good user experience (they can have ~30 seconds delay).
          // This action will tell the frontend that the claim has in fact expired, even if
          // the backend hasn't yet told us so. The claim implementation assumes
          // a reasonably synced frontend device clock. Worst case scenario should be that the
          // frontend allows the user to make changes when a claim by another user is in fact
          // still in effect, but those changes will then be rejected by the backend.
          expireClaim();
        }
      }, 500);
    }
    return () => clearInterval(interval);
  }, [
    calculateRemainingClaimTime,
    claimStatus?.claimed,
    isCollaborativeBet,
    expireClaim,
  ]);

  const [readUserCommentCount, setReadUserCommentCount] = useState(0);

  const setOpenState = visible => {
    if (!visible) {
      setReadUserCommentCount(
        comments.filter(comment => comment.commentType === 'UserMessage')
          .length,
      );
    }
    setOpen(visible);
  };

  if (!isCollaborativeBet) {
    // Either collaborators still loading,
    // or there's only one single collaborator (self), meaning this isn't a shared bet
    return null;
  }

  if (!claimStatus) {
    return null; // Without a claim status, collaboration can't be rendered
  }

  if (!Array.isArray(comments)) {
    return null; // Without successfully loaded comments, collaboration can't be rendered
  }

  const isReadOnly = collaborators.some(
    collaborator => collaborator.self && collaborator.type === 'READ',
  );

  const isOwner = collaborators.some(
    collaborator => collaborator.self && collaborator.owner,
  );

  return (
    <div>
      <div className={styles.collaborationButtonContainer}>
        <CollaborationButton
          onClick={() => setOpenState(!open)}
          collaborators={collaborators}
          showReadOnlyLock={isReadOnly || isLackingSubscription}
          comments={comments}
          readUserCommentCount={readUserCommentCount}
          isCollaborationCenterOpen={open}
          claimStatus={claimStatus}
        />
      </div>
      {open && (
        <div className={styles.collaborationCenterContainer}>
          <CollaborationCenter
            type={type}
            betId={betId}
            comments={comments}
            claimStatus={claimStatus}
            remainingClaimTime={remainingClaimTime}
            isLackingSubscription={isLackingSubscription}
            collaborators={collaborators}
            isReadOnly={isReadOnly}
            isOwner={isOwner}
            addComment={addComment}
            updateComment={updateComment}
            deleteComment={deleteComment}
            claimBet={claimBet}
            releaseBet={releaseBet}
            openShareDialog={openShareDialog}
            onClose={() => setOpenState(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Collaboration;
